import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import UserModal from './UserModal';
import RoleModal from './RoleModal';
import PermissionModal from './PermissionModal';

const UserManagement = () => {
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [permissions, setPermissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showUserModal, setShowUserModal] = useState(false);
  const [showRoleModal, setShowRoleModal] = useState(false);
  const [showPermissionModal, setShowPermissionModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedRole, setSelectedRole] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('All');
  const [roleFilter, setRoleFilter] = useState('All');
  const [currentUserRole, setCurrentUserRole] = useState('');
  const [accessDenied, setAccessDenied] = useState(false);

  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const API_BASE = process.env.REACT_APP_API_BASE;

  const fetchUsers = useCallback(async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('authToken');
      const response = await axios.get(`${API_BASE}/users.php`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        setUsers(response.data.users);
        setCurrentUserRole(response.data.current_user_role);

        // Check if user is Super Admin
        if (response.data.current_user_role !== 'Super Admin') {
          setAccessDenied(true);
        }
      }
    } catch (error) {
      if (error.response?.status === 403) {
        setAccessDenied(true);
      } else {
        setError('Failed to fetch users');
      }
      console.error('Error fetching users:', error);
    } finally {
      setLoading(false);
    }
  }, [API_BASE]);

  const fetchRoles = useCallback(async () => {
    try {
      const token = localStorage.getItem('authToken');
      const response = await axios.get(`${API_BASE}/user_roles.php`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        setRoles(response.data.roles);
        // Set current user role if not already set (using functional update to avoid dependency)
        setCurrentUserRole(prev => prev || response.data.current_user_role);
      }
    } catch (error) {
      if (error.response?.status === 403) {
        setAccessDenied(true);
      }
      console.error('Error fetching roles:', error);
    }
  }, [API_BASE]);

  const fetchPermissions = useCallback(async () => {
    try {
      const token = localStorage.getItem('authToken');
      const response = await axios.get(`${API_BASE}/user_permission.php`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        setPermissions(response.data.permissions);
      }
    } catch (error) {
      if (error.response?.status === 403) {
        setAccessDenied(true);
      }
      console.error('Error fetching permissions:', error);
    }
  }, [API_BASE]);

  useEffect(() => {
    fetchUsers();
    fetchRoles();
    fetchPermissions();
  }, [fetchUsers, fetchRoles, fetchPermissions]);

  // Check if current user has Super Admin access
  const isSuperAdmin = () => {
    return currentUserRole === 'Super Admin';
  };

  const handleDeleteUser = async (userId) => {
    if (!isSuperAdmin()) {
      setError('Access denied. Only Super Admins can delete users.');
      return;
    }

    if (window.confirm('Are you sure you want to delete this user?')) {
      try {
        const token = localStorage.getItem('authToken');
        const response = await axios.post(`${API_BASE}/delete_user.php`,
          { user_id: userId },
          { headers: { Authorization: `Bearer ${token}` } }
        );

        if (response.data.success) {
          fetchUsers();
        } else {
          setError(response.data.error || 'Failed to delete user');
        }
      } catch (error) {
        setError(error.response?.data?.error || 'Failed to delete user');
      }
    }
  };

  const handleToggleUserStatus = async (userId, currentStatus) => {
    if (!isSuperAdmin()) {
      setError('Access denied. Only Super Admins can modify user status.');
      return;
    }

    try {
      const token = localStorage.getItem('authToken');
      const response = await axios.post(`${API_BASE}/toggle_user_status.php`,
        {
          user_id: userId,
          status: currentStatus === 'Active' ? 'Inactive' : 'Active'
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        fetchUsers();
      } else {
        setError(response.data.error || 'Failed to update user status');
      }
    } catch (error) {
      setError(error.response?.data?.error || 'Failed to update user status');
    }
  };

  const handleEditUser = (user) => {
    if (!isSuperAdmin()) {
      setError('Access denied. Only Super Admins can edit users.');
      return;
    }
    setSelectedUser(user);
    setShowUserModal(true);
  };

  const handleEditRole = (role) => {
    if (!isSuperAdmin()) {
      setError('Access denied. Only Super Admins can manage roles.');
      return;
    }
    setSelectedRole(role);
    setShowRoleModal(true);
  };

  const handleManagePermissions = (role) => {
    if (!isSuperAdmin()) {
      setError('Access denied. Only Super Admins can manage permissions.');
      return;
    }
    setSelectedRole(role);
    setShowPermissionModal(true);
  };

  const resetUserModal = () => {
    setSelectedUser(null);
    setShowUserModal(false);
  };

  const resetRoleModal = () => {
    setSelectedRole(null);
    setShowRoleModal(false);
  };

  const resetPermissionModal = () => {
    setSelectedRole(null);
    setShowPermissionModal(false);
  };

  // Navigation handlers
  const goToDashboard = () => {
    navigate('/dashboard');
  };

  const goToHome = () => {
    navigate('/');
  };

  const handleLogout = () => {
    logout();
  };

  // Filter users
  const filteredUsers = users.filter(user => {
    const matchesSearch =
      user.username?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.email?.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesStatus = statusFilter === 'All' || user.status === statusFilter;

    const matchesRole = roleFilter === 'All' || user.role_id?.toString() === roleFilter;

    return matchesSearch && matchesStatus && matchesRole;
  });

  // Get role name by ID
  const getRoleName = (roleId) => {
    const role = roles.find(r => r.role_id === roleId);
    return role ? role.role_name : 'No Role';
  };

  // Get permissions for a role
  const getRolePermissions = (roleId) => {
    return permissions.filter(p => p.role_id === roleId);
  };

  // Get module names for a role
  const getRoleModuleNames = (roleId) => {
    const rolePerms = getRolePermissions(roleId);
    return rolePerms.map(p => p.module_name).join(', ');
  };

  // Show access denied message
  if (accessDenied) {
    return (
      <div className="dashboard-container">
        {/* Navigation Bar */}
        <nav className="navbar navbar-expand-lg navbar-dark bg-primary shadow">
          <div className="container-fluid">
            <Link to="/" className="navbar-brand mb-0 h1 text-decoration-none">
              <i className="bi bi-building me-2"></i>
              HRMS Dashboard
            </Link>

            <div className="d-none d-lg-flex align-items-center">
              <div className="navbar-nav me-3">
                <Link to="/" className="nav-link text-light">
                  <i className="bi bi-house me-1"></i>
                  Home
                </Link>
                <Link to="/user-management" className="nav-link text-light active">
                  <i className="bi bi-person-gear me-1"></i>
                  User Management
                </Link>
              </div>

              <div className="dropdown">
                <button
                  className="btn btn-outline-light dropdown-toggle d-flex align-items-center"
                  type="button"
                  data-bs-toggle="dropdown"
                  aria-expanded="false"
                >
                  <i className="bi bi-person-circle me-2"></i>
                  {user?.username || 'User'}
                </button>
                <ul className="dropdown-menu dropdown-menu-end">
                  <li>
                    <button className="dropdown-item text-danger" onClick={handleLogout}>
                      <i className="bi bi-box-arrow-right me-2"></i>Logout
                    </button>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </nav>

        {/* Access Denied Content */}
        <div className="container-fluid mt-4">
          <div className="row justify-content-center">
            <div className="col-md-6">
              <div className="card shadow-sm border-0">
                <div className="card-body text-center py-5">
                  <i className="bi bi-shield-exclamation display-1 text-warning"></i>
                  <h3 className="mt-4 text-dark">Access Denied</h3>
                  <p className="text-muted mb-4">
                    You need <strong>Super Admin</strong> privileges to access the User Management page.
                  </p>
                  <p className="text-muted small mb-4">
                    Your current role: <strong>{currentUserRole || 'Loading...'}</strong>
                  </p>
                  <div className="d-flex justify-content-center gap-3">
                    <button className="btn btn-primary" onClick={goToHome}>
                      <i className="bi bi-house me-2"></i>
                      Go to Home
                    </button>
                    <button className="btn btn-outline-secondary" onClick={goToDashboard}>
                      <i className="bi bi-speedometer2 me-2"></i>
                      Go to Dashboard
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center youtube-page-container" style={{ height: '50vh' }}>
        <div className="text-center">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="mt-2 text-muted">Loading user data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard-container">
      {/* Navigation Bar */}
      <nav className="navbar navbar-expand-lg navbar-dark bg-primary shadow">
        <div className="container-fluid">
          <Link to="/" className="navbar-brand mb-0 h1 text-decoration-none">
            <i className="bi bi-building me-2"></i>
            HRMS Dashboard
          </Link>

          <div className="d-none d-lg-flex align-items-center">
            <div className="navbar-nav me-3">
              <Link to="/" className="nav-link text-light">
                <i className="bi bi-house me-1"></i>
                Home
              </Link>
              <Link to="/employees" className="nav-link text-light">
                <i className="bi bi-people me-1"></i>
                Employees
              </Link>
              <Link to="/attendance" className="nav-link text-light">
                <i className="bi bi-calendar-check me-1"></i>
                Attendance
              </Link>
              <Link to="/user-management" className="nav-link text-light active">
                <i className="bi bi-person-gear me-1"></i>
                User Management
              </Link>
              <Link to="/menu" className="nav-link text-light">
                <i className="bi bi-list me-1"></i>
                Menu
              </Link>
            </div>

            <div className="dropdown">
              <button
                className="btn btn-outline-light dropdown-toggle d-flex align-items-center"
                type="button"
                data-bs-toggle="dropdown"
                aria-expanded="false"
              >
                <i className="bi bi-person-circle me-2"></i>
                {user?.username || 'User'} {currentUserRole && `(${currentUserRole})`}
              </button>
              <ul className="dropdown-menu dropdown-menu-end">
                <li>
                  <Link className="dropdown-item" to="/company-settings">
                    <i className="bi bi-person me-2"></i>Profile
                  </Link>
                </li>
                <li>
                  <Link className="dropdown-item" to="/admin-panel">
                    <i className="bi bi-gear me-2"></i>Settings
                  </Link>
                </li>
                <li><hr className="dropdown-divider" /></li>
                <li>
                  <button className="dropdown-item text-danger" onClick={handleLogout}>
                    <i className="bi bi-box-arrow-right me-2"></i>Logout
                  </button>
                </li>
              </ul>
            </div>
          </div>

          <div className="d-lg-none">
            <Link to="/menu" className="btn btn-outline-light btn-sm">
              <i className="bi bi-list"></i>
            </Link>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <div className="container-fluid mt-4" style={{ paddingBottom: '80px' }}>
        {/* Header Section */}
        <div className="row mb-4">
          <div className="col-12">
            <div className="card shadow-sm border-0">
              <div className="card-body py-4">
                <div className="row align-items-center">
                  <div className="col-md-6">
                    <div className="d-flex align-items-center">
                      <div className="btn-group me-3" role="group">
                        <button
                          className="btn btn-outline-secondary btn-sm"
                          onClick={goToHome}
                          title="Go to Home"
                        >
                          <i className="bi bi-house"></i>
                        </button>
                        <button
                          className="btn btn-outline-secondary btn-sm"
                          onClick={goToDashboard}
                          title="Go to Dashboard"
                        >
                          <i className="bi bi-speedometer2"></i>
                        </button>
                      </div>
                      <div>
                        <h2 className="h4 mb-1 fw-bold text-dark">
                          <i className="bi bi-person-gear me-2 text-primary"></i>
                          User Management & Permissions
                        </h2>
                        <p className="text-muted mb-0">
                          Manage system users, roles, and permissions
                        </p>
                      </div>
                    </div>
                  </div>
                  <div className="col-md-6 text-end">
                    <div className="d-flex justify-content-end align-items-center">
                      <button
                        className="btn btn-warning me-2"
                        onClick={() => setShowPermissionModal(true)}
                        disabled={!isSuperAdmin()}
                        title={!isSuperAdmin() ? "Super Admin access required" : "Manage Permissions"}
                      >
                        <i className="bi bi-shield-check me-2"></i>
                        Manage Permissions
                      </button>
                      <button
                        className="btn btn-info me-2"
                        onClick={() => setShowRoleModal(true)}
                        disabled={!isSuperAdmin()}
                        title={!isSuperAdmin() ? "Super Admin access required" : "Manage Roles"}
                      >
                        <i className="bi bi-people me-2"></i>
                        Manage Roles
                      </button>
                      <button
                        className="btn btn-primary"
                        onClick={() => setShowUserModal(true)}
                        disabled={!isSuperAdmin()}
                        title={!isSuperAdmin() ? "Super Admin access required" : "Add User"}
                      >
                        <i className="bi bi-person-plus me-2"></i>
                        Add User
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Quick Navigation */}
        <div className="row mb-4">
          <div className="col-12">
            <div className="card bg-light border-0">
              <div className="card-body py-3">
                <div className="row text-center">
                  <div className="col-md-3 mb-2 mb-md-0">
                    <button
                      className="btn btn-outline-primary w-100 d-flex align-items-center justify-content-center"
                      onClick={goToHome}
                    >
                      <i className="bi bi-house me-2"></i>
                      Home
                    </button>
                  </div>
                  <div className="col-md-3 mb-2 mb-md-0">
                    <button
                      className="btn btn-outline-success w-100 d-flex align-items-center justify-content-center"
                      onClick={goToDashboard}
                    >
                      <i className="bi bi-speedometer2 me-2"></i>
                      Dashboard
                    </button>
                  </div>
                  <div className="col-md-3 mb-2 mb-md-0">
                    <Link
                      to="/attendance"
                      className="btn btn-outline-warning w-100 d-flex align-items-center justify-content-center"
                    >
                      <i className="bi bi-calendar-check me-2"></i>
                      Attendance
                    </Link>
                  </div>
                  <div className="col-md-3">
                    <Link
                      to="/user-management"
                      className="btn btn-primary w-100 d-flex align-items-center justify-content-center"
                    >
                      <i className="bi bi-person-gear me-2"></i>
                      User Management
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="row mb-4">
          <div className="col-12">
            <div className="card shadow-sm border-0">
              <div className="card-body">
                <div className="row g-3 align-items-center">
                  <div className="col-md-4">
                    <div className="input-group">
                      <span className="input-group-text bg-light border-end-0">
                        <i className="bi bi-search text-muted"></i>
                      </span>
                      <input
                        type="text"
                        className="form-control border-start-0"
                        placeholder="Search users..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                      />
                    </div>
                  </div>
                  <div className="col-md-3">
                    <select
                      className="form-select"
                      value={statusFilter}
                      onChange={(e) => setStatusFilter(e.target.value)}
                    >
                      <option value="All">All Status</option>
                      <option value="Active">Active</option>
                      <option value="Inactive">Inactive</option>
                      <option value="Locked">Locked</option>
                    </select>
                  </div>
                  <div className="col-md-3">
                    <select
                      className="form-select"
                      value={roleFilter}
                      onChange={(e) => setRoleFilter(e.target.value)}
                    >
                      <option value="All">All Roles</option>
                      {roles.filter(r => r.is_active).map(role => (
                        <option key={role.role_id} value={role.role_id}>
                          {role.role_name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="col-md-2 text-end">
                    <button
                      className="btn btn-outline-secondary"
                      onClick={() => {
                        fetchUsers();
                        fetchRoles();
                        fetchPermissions();
                      }}
                      title="Refresh"
                    >
                      <i className="bi bi-arrow-clockwise"></i>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Error Alert */}
        {error && (
          <div className="row mb-4">
            <div className="col-12">
              <div className="alert alert-danger d-flex align-items-center" role="alert">
                <i className="bi bi-exclamation-triangle-fill me-2"></i>
                <div>{error}</div>
                <button
                  type="button"
                  className="btn-close ms-auto"
                  onClick={() => setError('')}
                ></button>
              </div>
            </div>
          </div>
        )}

        {/* Users and Roles Grid */}
        <div className="row">
          {/* Users List */}
          <div className="col-lg-8 mb-4">
            <div className="card shadow-sm border-0 h-100">
              <div className="card-header bg-white border-0 py-3">
                <h5 className="card-title mb-0">
                  <i className="bi bi-people me-2 text-primary"></i>
                  System Users ({filteredUsers.length})
                </h5>
              </div>
              <div className="card-body p-0">
                {filteredUsers.length === 0 ? (
                  <div className="text-center py-5">
                    <i className="bi bi-people display-1 text-muted"></i>
                    <h4 className="mt-3 text-muted">No users found</h4>
                    <p className="text-muted">
                      {users.length === 0
                        ? "Get started by adding your first system user."
                        : "No users match your search criteria."
                      }
                    </p>
                    {users.length === 0 && isSuperAdmin() && (
                      <button
                        className="btn btn-primary mt-3"
                        onClick={() => setShowUserModal(true)}
                      >
                        <i className="bi bi-person-plus me-2"></i>
                        Add First User
                      </button>
                    )}
                  </div>
                ) : (
                  <div className="table-responsive">
                    <table className="table table-hover align-middle mb-0">
                      <thead className="bg-light">
                        <tr>
                          <th className="ps-4">User</th>
                          <th>Role</th>
                          <th>Status</th>
                          <th>Last Login</th>
                          <th>Created</th>
                          <th className="text-end pe-4">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredUsers.map((user) => (
                          <tr key={user.user_id}>
                            <td className="ps-4">
                              <div className="d-flex align-items-center">
                                <div
                                  className="user-avatar bg-primary text-white rounded-circle d-flex align-items-center justify-content-center me-3"
                                  style={{ width: '40px', height: '40px' }}
                                >
                                  {user.username?.charAt(0).toUpperCase()}
                                </div>
                                <div>
                                  <div className="fw-semibold">{user.username}</div>
                                  <small className="text-muted">{user.email}</small>
                                </div>
                              </div>
                            </td>
                            <td>
                              <span className="badge bg-info">
                                {getRoleName(user.role_id)}
                              </span>
                            </td>
                            <td>
                              <span className={`badge ${user.status === 'Active' ? 'bg-success' :
                                user.status === 'Inactive' ? 'bg-secondary' : 'bg-danger'
                                }`}>
                                {user.status}
                              </span>
                            </td>
                            <td>
                              <small className="text-muted">
                                {user.last_login ? new Date(user.last_login).toLocaleDateString() : 'Never'}
                              </small>
                            </td>
                            <td>
                              <small className="text-muted">
                                {new Date(user.created_at).toLocaleDateString()}
                              </small>
                            </td>
                            <td className="text-end pe-4">
                              <div className="btn-group" role="group">
                                <button
                                  className="btn btn-outline-primary btn-sm"
                                  onClick={() => handleEditUser(user)}
                                  title="Edit"
                                  disabled={!isSuperAdmin()}
                                >
                                  <i className="bi bi-pencil"></i>
                                </button>
                                <button
                                  className={`btn btn-sm ${user.status === 'Active'
                                    ? 'btn-outline-warning'
                                    : 'btn-outline-success'
                                    }`}
                                  onClick={() => handleToggleUserStatus(user.user_id, user.status)}
                                  title={user.status === 'Active' ? 'Deactivate' : 'Activate'}
                                  disabled={!isSuperAdmin()}
                                >
                                  <i className={`bi ${user.status === 'Active' ? 'bi-pause' : 'bi-play'
                                    }`}></i>
                                </button>
                                <button
                                  className="btn btn-outline-danger btn-sm"
                                  onClick={() => handleDeleteUser(user.user_id)}
                                  title="Delete"
                                  disabled={!isSuperAdmin()}
                                >
                                  <i className="bi bi-trash"></i>
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Roles and Permissions */}
          <div className="col-lg-4 mb-4">
            <div className="card shadow-sm border-0 h-100">
              <div className="card-header bg-white border-0 py-3">
                <h5 className="card-title mb-0">
                  <i className="bi bi-shield-check me-2 text-success"></i>
                  System Roles ({roles.length})
                </h5>
              </div>
              <div className="card-body p-0">
                {roles.length === 0 ? (
                  <div className="text-center py-5">
                    <i className="bi bi-shield-check display-1 text-muted"></i>
                    <h4 className="mt-3 text-muted">No roles defined</h4>
                    <p className="text-muted">
                      Create roles to manage user permissions
                    </p>
                    {isSuperAdmin() && (
                      <button
                        className="btn btn-info mt-3"
                        onClick={() => setShowRoleModal(true)}
                      >
                        <i className="bi bi-people me-2"></i>
                        Create First Role
                      </button>
                    )}
                  </div>
                ) : (
                  <div className="list-group list-group-flush">
                    {roles.map((role) => (
                      <div key={role.role_id} className="list-group-item border-0">
                        <div className="d-flex justify-content-between align-items-start">
                          <div className="flex-grow-1">
                            <h6 className="mb-1">
                              {role.role_name}
                              {!role.is_active && (
                                <span className="badge bg-secondary ms-2">Inactive</span>
                              )}
                            </h6>
                            <p className="text-muted small mb-2">{role.description}</p>
                            <div className="small">
                              <strong>Permissions:</strong>
                              {getRolePermissions(role.role_id).length > 0 ? (
                                <div className="mt-1">
                                  <span className="text-success">
                                    {getRolePermissions(role.role_id).length} modules
                                  </span>
                                  <div className="text-muted small mt-1">
                                    {getRoleModuleNames(role.role_id)}
                                  </div>
                                </div>
                              ) : (
                                <span className="text-warning ms-2">No permissions</span>
                              )}
                            </div>
                          </div>
                          <div className="btn-group ms-2">
                            <button
                              className="btn btn-outline-primary btn-sm"
                              onClick={() => handleEditRole(role)}
                              title="Edit Role"
                              disabled={!isSuperAdmin()}
                            >
                              <i className="bi bi-pencil"></i>
                            </button>
                            <button
                              className="btn btn-outline-success btn-sm"
                              onClick={() => handleManagePermissions(role)}
                              title="Manage Permissions"
                              disabled={!isSuperAdmin()}
                            >
                              <i className="bi bi-shield-check"></i>
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Stats Footer */}
        <div className="row mt-4">
          <div className="col-12">
            <div className="card bg-light border-0">
              <div className="card-body py-3">
                <div className="row text-center">
                  <div className="col-md-3">
                    <div className="fw-bold text-primary fs-4">{filteredUsers.length}</div>
                    <small className="text-muted">Showing Users</small>
                  </div>
                  <div className="col-md-3">
                    <div className="fw-bold text-success fs-4">{users.length}</div>
                    <small className="text-muted">Total Users</small>
                  </div>
                  <div className="col-md-3">
                    <div className="fw-bold text-info fs-4">{roles.length}</div>
                    <small className="text-muted">System Roles</small>
                  </div>
                  <div className="col-md-3">
                    <div className="fw-bold text-warning fs-4">{permissions.length}</div>
                    <small className="text-muted">Permission Rules</small>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Modals */}
      <UserModal
        show={showUserModal}
        onHide={resetUserModal}
        user={selectedUser}
        roles={roles}
        onSuccess={() => {
          fetchUsers();
          resetUserModal();
        }}
        onError={setError}
      />

      <RoleModal
        show={showRoleModal}
        onHide={resetRoleModal}
        role={selectedRole}
        onSuccess={() => {
          fetchRoles();
          resetRoleModal();
        }}
        onError={setError}
      />

      <PermissionModal
        show={showPermissionModal}
        onHide={resetPermissionModal}
        role={selectedRole}
        roles={roles}
        permissions={permissions}
        onSuccess={() => {
          fetchPermissions();
          resetPermissionModal();
        }}
        onError={setError}
      />
    </div>
  );
};

export default UserManagement;